import {Phone, Calendar, Shield, Check, Zap, MapPin, Car, Award} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const TeslaModelSPpfPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does Tesla Model S PPF cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Model S partial front coverage starts at $1,299, full front starts at $1,899, and full vehicle PPF starts at $4,499. Model S's larger sedan surface area can place final pricing above these starting points."
          }
        },
        {
          "@type": "Question",
          "name": "Is PPF worth it for a luxury Model S Plaid?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. Model S Plaid represents a $100,000+ investment. PPF protects this investment and prevents paint damage that reduces resale value by thousands of dollars. Most Plaid owners choose full vehicle coverage."
          }
        },
        {
          "@type": "Question",
          "name": "How long does Model S PPF take to install?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front takes 1-2 days, full front requires 2-3 days, and full vehicle PPF takes 4-6 days for Model S due to larger surface area and complex curves. Quality installation cannot be rushed."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Tesla Model S PPF San Diego | Luxury Paint Protection Film"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium Tesla Model S PPF installation in San Diego. Protect your luxury Model S sedan with professional paint protection film. Self-healing technology, manufacturer-backed film warranties. Serving Vista, Carlsbad, La Jolla.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/tesla-model-s-ppf-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Tesla Model S PPF San Diego - Luxury Paint Protection Film Installation</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your Tesla Model S with premium paint protection film installation in San Diego. Our installers specialize in luxury sedan PPF, delivering careful protection for Model S, Model S Long Range, and Model S Plaid with precision craftsmanship and attention to detail.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              The Model S represents Tesla's flagship luxury sedan, combining performance, elegance, and cutting-edge technology. Our Model S PPF installations use the highest-grade self-healing film and expert techniques to protect your investment while maintaining the refined appearance that defines this premium vehicle. Serving discerning owners in La Jolla, Rancho Santa Fe, Del Mar, and throughout San Diego County.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Model S PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Model S Needs PPF */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Model S Owners Choose Premium PPF Protection</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Tesla Model S ownership represents a significant investment in luxury electric performance. However, Model S shares Tesla's characteristic thin, water-based paint that's highly susceptible to rock chips, scratches, and environmental damage. <a href="/paint-protection-film" className="text-cyan-400 hover:underline">Paint protection film</a> provides invisible armor that preserves your Model S's elegant appearance and protects resale value over years of ownership.
            </p>
            <p>
              Model S sedans in San Diego face challenging conditions: highway debris on I-5 and I-15, coastal salt spray from beach drives to La Jolla and Del Mar, parking lot door dings in Rancho Santa Fe shopping areas, and intense UV exposure year-round. Premium PPF shields against all these threats while maintaining the sophisticated finish that makes the Model S a statement vehicle.
            </p>
            <p>
              Our Model S PPF installations demand precision craftsmanship. The sedan's large hood, complex fender curves, and refined body lines require expert handling and Model S-specific bulk or precision-pattern methods. We protect vulnerable areas including the extensive front fascia, long hood surface, door edges, rear bumper, and trunk lid—ensuring comprehensive coverage with invisible seams and perfect edge finishing.
            </p>
            <p>
              Model S Plaid owners typically choose full vehicle PPF to match their performance investment, while Long Range owners often select full front coverage as the sweet spot between protection and value. Combining <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF</a> with <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> and <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> creates a fully protected, customized luxury sedan worthy of the Model S badge.
            </p>
          </div>
        </div>
      </section>

      {/* Model S Coverage Options & Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Model S PPF Coverage & Pricing</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front Protection</h3>
              <p className="text-gray-300 mb-6">
                Essential Model S coverage for high-impact zones: front bumper, partial hood (18-24"), mirrors, and headlights. Protects the most vulnerable areas from highway rock chips.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Complete front bumper shield</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (18-24 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Side mirrors and headlights</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Premium self-healing film</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">RECOMMENDED</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front Protection</h3>
              <p className="text-gray-300 mb-6">
                Comprehensive Model S front-end protection: full hood, bumper, fenders, mirrors, and lights. The ideal choice for luxury sedan owners seeking complete highway armor.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All partial coverage included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>A-pillars and rocker panels</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>10-year manufacturer warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Protection</h3>
              <p className="text-gray-300 mb-6">
                Ultimate Model S protection: every painted surface wrapped in premium PPF. Perfect for Plaid owners and those demanding absolute paint perfection and maximum resale value.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front coverage areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Doors, rockers, rear quarters</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, trunk, rear bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Permanent investment protection</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            All packages include professional paint preparation, precision installation, and manufacturer warranty. Starting prices shown are for Tesla Model S. Exact pricing determined after consultation and vehicle inspection.
          </p>
        </div>
      </section>

      {/* Model S Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Luxury Model S Paint Protection Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Large Surface Protection</h3>
              <p className="text-gray-300">Model S's expansive hood and long fenders require expert installation. PPF provides seamless coverage across large panels without visible seams.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Luxury Finish Preservation</h3>
              <p className="text-gray-300">Premium paint colors like Deep Blue Metallic and Red Multi-Coat maintain depth and gloss under PPF's protective barrier for years.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Technology</h3>
              <p className="text-gray-300">Minor scratches and swirl marks disappear with heat, maintaining showroom perfection expected from a flagship luxury electric sedan.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Investment Protection</h3>
              <p className="text-gray-300">Model S Plaid represents $100,000+ investment. PPF prevents thousands in paint damage and maintains premium resale value for future buyers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Model S PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Luxury Vehicle Consultation</h3>
                <p className="text-gray-300">We conduct a comprehensive inspection of your Model S, evaluating paint condition, discussing your driving patterns and protection goals, and recommending coverage options appropriate for a luxury flagship sedan. You receive a detailed quote with premium film options and complete pricing transparency.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Premium Paint Preparation</h3>
                <p className="text-gray-300">Your Model S receives meticulous preparation: hand washing, full clay bar treatment, paint decontamination, and multi-stage paint correction if needed. We remove all swirl marks, water spots, and imperfections to create a flawless surface. Model S deserves perfection—we deliver it before PPF application.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Expert PPF Installation</h3>
                <p className="text-gray-300">Using Model S-specific bulk or precision-pattern methods and advanced wet installation techniques, we apply premium self-healing PPF with precision edge wrapping. Large panels like the hood require expert handling to eliminate bubbles and ensure perfect adhesion. We properly disassemble trim pieces, badges, and sensors for seamless edge-to-edge coverage that's completely invisible.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Assurance & White-Glove Delivery</h3>
                <p className="text-gray-300">Rigorous multi-point inspection ensures flawless edges, bubble-free application, perfect adhesion, and proper sensor function. We verify all cameras and Autopilot systems work correctly, provide detailed care instructions, deliver warranty documentation, and return your Model S in pristine, protected condition.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model S Specific Protection */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Critical Model S Protection Zones</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Expansive Hood & Front End</h3>
              <p className="text-gray-300 mb-4">Model S features Tesla's largest hood and most prominent front fascia. These expansive surfaces face constant assault from freeway debris, rocks, and bugs during San Diego highway driving and long-distance travel.</p>
              <p className="text-gray-300">Full hood and front bumper PPF creates comprehensive protection across the entire front end, preventing the dozens of rock chips that would otherwise appear within the first year of Model S ownership.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Door Edges & Handles</h3>
              <p className="text-gray-300 mb-4">Model S's flush handles and painted door edges chip easily from jewelry, keys, and contact with adjacent vehicles in La Jolla shopping centers and Rancho Santa Fe parking areas.</p>
              <p className="text-gray-300">Door edge PPF prevents unsightly chips and preserves the clean body lines that define the Model S's elegant sedan silhouette. Protection that maintains luxury appearance.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Complex Fender Curves</h3>
              <p className="text-gray-300 mb-4">Model S fenders feature compound curves and wheel well arches that require expert PPF installation. Improper application creates visible seams and premature edge lifting.</p>
              <p className="text-gray-300">Our installers have years of luxury sedan experience, applying PPF to Model S fenders with invisible seams and perfect contour following for seamless, long-lasting protection.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Trunk & Rear Bumper</h3>
              <p className="text-gray-300 mb-4">Model S trunk lids and rear bumpers suffer from scratches during loading, contact with luggage, golf bags, and everyday cargo. Without protection, these high-contact areas show wear quickly.</p>
              <p className="text-gray-300">Rear PPF creates a scratch-resistant barrier on the trunk edge and rear bumper, maintaining pristine appearance in loading zones that receive frequent contact throughout ownership.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Model S PPF FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does Tesla Model S PPF cost in San Diego?</h3>
              <p className="text-gray-300">Model S partial front coverage starts at $1,299, full front starts at $1,899, and full vehicle PPF starts at $4,499. Model S's larger sedan surface area and complex curves can place final pricing above these starting points. Final cost depends on paint condition, coverage selection, and any paint correction needs. We provide complete quotes with no hidden fees.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is PPF worth it for a luxury Model S Plaid?</h3>
              <p className="text-gray-300">Absolutely. Model S Plaid represents a $100,000+ investment in performance and luxury. PPF protects this investment from paint damage that reduces resale value by thousands of dollars. Most Plaid owners choose full vehicle coverage to maintain museum-quality appearance and protect every surface of their flagship sedan.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does Model S PPF installation take?</h3>
              <p className="text-gray-300">Partial front takes 1-2 days, full front requires 2-3 days, and full vehicle PPF takes 4-6 days for Model S due to larger surface area, complex curves, and meticulous quality requirements for luxury vehicles. Proper installation and curing time ensures bubble-free, long-lasting results worthy of the Model S standard.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will PPF affect my Model S's appearance?</h3>
              <p className="text-gray-300">No. Premium PPF is optically clear and actually enhances gloss depth, especially on dark colors like Solid Black and Deep Blue Metallic. The film is completely invisible when properly installed—most people cannot detect PPF even at close inspection. Your Model S maintains its factory elegance with invisible protection.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can PPF be applied to a used Model S?</h3>
              <p className="text-gray-300">Yes. We perform comprehensive paint correction to remove existing swirl marks, scratches, and oxidation before PPF installation, restoring like-new appearance. PPF then locks in the corrected finish and prevents future damage. Many used Model S buyers immediately protect their purchase with full vehicle PPF.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What warranty comes with Model S PPF?</h3>
              <p className="text-gray-300">Premium PPF includes a 10-year manufacturer warranty against yellowing, cracking, peeling, and delamination. Our installation workmanship is also guaranteed for the warranty period—we repair any bubbling, lifting, or edge issues at no charge. Model S deserves the best protection, backed by the strongest warranty.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Tesla Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/tesla-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Professional PPF for all Tesla models: Model S, 3, X, Y, and Cybertruck</p>
            </a>
            <a href="/paint-protection-film-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Premium PPF installation for all luxury and performance vehicles</p>
            </a>
            <a href="/chrome-delete-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete San Diego</h3>
              <p className="text-gray-400 text-sm">Blacked-out trim customization for sleek Model S aesthetic</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Tesla Model S PPF Installation Throughout San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Located in Vista, we provide expert Model S paint protection film services to luxury vehicle owners in La Jolla, Rancho Santa Fe, Del Mar, and throughout San Diego County.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-center">
            <div>
              <p className="font-semibold text-white mb-2">Luxury Communities</p>
              <p className="text-sm"><a href="/ppf-la-jolla" className="text-cyan-400 hover:underline">La Jolla</a> • <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a> • <a href="/ppf-del-mar" className="text-cyan-400 hover:underline">Del Mar</a> • <a href="/ppf-solana-beach" className="text-cyan-400 hover:underline">Solana Beach</a></p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">North County</p>
              <p className="text-sm"><a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> • <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a> • <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a> • <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a> • <a href="/ppf-san-marcos" className="text-cyan-400 hover:underline">San Marcos</a> • <a href="/ppf-escondido" className="text-cyan-400 hover:underline">Escondido</a></p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">Greater San Diego</p>
              <p className="text-sm"><a href="/ppf-poway" className="text-cyan-400 hover:underline">Poway</a> • <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">San Diego</a> • Carmel Valley • Scripps Ranch</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Tesla Model S Investment</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free Model S PPF consultation and receive a custom quote for your luxury flagship sedan. Same-week appointments available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Model S PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default TeslaModelSPpfPage
